import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  feedStockApi, 
  FeedStock, 
  CreateFeedStockDto, 
  UpdateFeedStockDto,
  FeedStockFilters,
  RecordFeedStockUsageDto,
  RecordFeedStockCountDto,
  RestockFeedStockDto
} from '../api/feedStock';

interface UseFeedStockReturn {
  // Data state
  feedStocks: FeedStock[];
  loading: boolean;
  error: string | null;
  
  // Statistics
  statistics: {
    totalItems: number;
    lowStockItems: number;
    outOfStockItems: number;
    expiredItems: number;
    expiringSoonItems: number;
    totalValue: number;
  };
  
  // CRUD operations
  loadFeedStocks: (filters?: FeedStockFilters) => Promise<void>;
  createFeedStock: (data: CreateFeedStockDto) => Promise<FeedStock | null>;
  updateFeedStock: (id: string, data: UpdateFeedStockDto) => Promise<FeedStock | null>;
  deleteFeedStock: (id: string) => Promise<boolean>;
  
  // Transaction operations
  recordUsage: (id: string, data: RecordFeedStockUsageDto) => Promise<FeedStock | null>;
  recordCount: (id: string, data: RecordFeedStockCountDto) => Promise<FeedStock | null>;
  restockItem: (id: string, data: RestockFeedStockDto) => Promise<FeedStock | null>;
  
  // Utility functions
  getFeedStockById: (id: string) => FeedStock | undefined;
  getFeedStockStatus: (stock: FeedStock) => 'in_stock' | 'low_stock' | 'out_of_stock' | 'expired';
  isExpiringSoon: (expiryDate: string) => boolean;
  isExpired: (expiryDate: string) => boolean;
  
  // Refresh data
  refreshData: () => Promise<void>;
}

export const useFeedStock = (): UseFeedStockReturn => {
  const [feedStocks, setFeedStocks] = useState<FeedStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Utility functions
  const isExpiringSoon = useCallback((expiryDate: string): boolean => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  }, []);

  const isExpired = useCallback((expiryDate: string): boolean => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  }, []);

  const getFeedStockStatus = useCallback((stock: FeedStock): 'in_stock' | 'low_stock' | 'out_of_stock' | 'expired' => {
    if (stock.expirationDate && isExpired(stock.expirationDate)) {
      return 'expired';
    }
    
    // Convert to numbers to ensure proper comparison
    const quantity = Number(stock.quantity || 0);
    const reorderPoint = Number(stock.reorderPoint || 0);
    
    if (quantity === 0) {
      return 'out_of_stock';
    }
    if (quantity <= reorderPoint) {
      return 'low_stock';
    }
    return 'in_stock';
  }, [isExpired]);

  const getFeedStockById = useCallback((id: string): FeedStock | undefined => {
    return feedStocks.find(stock => stock.id === id);
  }, [feedStocks]);

  // Calculate statistics
  const statistics = useCallback(() => {
    const stats = {
      totalItems: feedStocks.length,
      lowStockItems: 0,
      outOfStockItems: 0,
      expiredItems: 0,
      expiringSoonItems: 0,
      totalValue: 0,
    };

    feedStocks.forEach(stock => {
      const status = getFeedStockStatus(stock);
      
      if (status === 'low_stock') stats.lowStockItems++;
      if (status === 'out_of_stock') stats.outOfStockItems++;
      if (status === 'expired') stats.expiredItems++;
      
      if (stock.expirationDate && !isExpired(stock.expirationDate) && isExpiringSoon(stock.expirationDate)) {
        stats.expiringSoonItems++;
      }
      
      // Calculate current inventory value (current quantity × unit price)
      const currentInventoryValue = Number(stock.quantity || 0) * Number(stock.unitPrice || 0);
      stats.totalValue += currentInventoryValue;
    });

    return stats;
  }, [feedStocks, getFeedStockStatus, isExpired, isExpiringSoon])();

  // Load feed stocks with optional filters
  const loadFeedStocks = useCallback(async (filters?: FeedStockFilters) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await feedStockApi.getAll(filters);
      
      // Add computed status to each feed stock item
      const feedStocksWithStatus = response.data.map(stock => ({
        ...stock,
        status: getFeedStockStatus(stock)
      }));
      
      setFeedStocks(feedStocksWithStatus);
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to load feed stocks';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getFeedStockStatus]);

  // Create new feed stock
  const createFeedStock = useCallback(async (data: CreateFeedStockDto): Promise<FeedStock | null> => {
    try {
      const response = await feedStockApi.create(data);
      const newStock = {
        ...response.data,
        status: getFeedStockStatus(response.data)
      };
      
      setFeedStocks(prev => [...prev, newStock]);
      toast.success('Feed stock item created successfully');
      return newStock;
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to create feed stock';
      toast.error(errorMessage);
      return null;
    }
  }, [getFeedStockStatus]);

  // Update feed stock
  const updateFeedStock = useCallback(async (id: string, data: UpdateFeedStockDto): Promise<FeedStock | null> => {
    try {
      const response = await feedStockApi.update(id, data);
      const updatedStock = {
        ...response.data,
        status: getFeedStockStatus(response.data)
      };
      
      setFeedStocks(prev => prev.map(stock => 
        stock.id === id ? updatedStock : stock
      ));
      toast.success('Feed stock updated successfully');
      return updatedStock;
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to update feed stock';
      toast.error(errorMessage);
      return null;
    }
  }, [getFeedStockStatus]);

  // Delete feed stock
  const deleteFeedStock = useCallback(async (id: string): Promise<boolean> => {
    try {
      await feedStockApi.delete(id);
      setFeedStocks(prev => prev.filter(stock => stock.id !== id));
      toast.success('Feed stock deleted successfully');
      return true;
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to delete feed stock';
      toast.error(errorMessage);
      return false;
    }
  }, []);

  // Record usage
  const recordUsage = useCallback(async (id: string, data: RecordFeedStockUsageDto): Promise<FeedStock | null> => {
    try {
      const response = await feedStockApi.recordUsage(id, data);
      const updatedStock = {
        ...response.data,
        status: getFeedStockStatus(response.data)
      };
      
      setFeedStocks(prev => prev.map(stock => 
        stock.id === id ? updatedStock : stock
      ));
      toast.success('Usage recorded successfully');
      return updatedStock;
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to record usage';
      toast.error(errorMessage);
      return null;
    }
  }, [getFeedStockStatus]);

  // Record count
  const recordCount = useCallback(async (id: string, data: RecordFeedStockCountDto): Promise<FeedStock | null> => {
    try {
      const response = await feedStockApi.recordCount(id, data);
      const updatedStock = {
        ...response.data,
        status: getFeedStockStatus(response.data)
      };
      
      setFeedStocks(prev => prev.map(stock => 
        stock.id === id ? updatedStock : stock
      ));
      toast.success('Stock count updated successfully');
      return updatedStock;
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to update stock count';
      toast.error(errorMessage);
      return null;
    }
  }, [getFeedStockStatus]);

  // Restock item
  const restockItem = useCallback(async (id: string, data: RestockFeedStockDto): Promise<FeedStock | null> => {
    try {
      const response = await feedStockApi.restock(id, data);
      const updatedStock = {
        ...response.data,
        status: getFeedStockStatus(response.data)
      };
      
      setFeedStocks(prev => prev.map(stock => 
        stock.id === id ? updatedStock : stock
      ));
      toast.success('Stock restocked successfully');
      return updatedStock;
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to restock item';
      toast.error(errorMessage);
      return null;
    }
  }, [getFeedStockStatus]);

  // Refresh data
  const refreshData = useCallback(async () => {
    await loadFeedStocks();
  }, [loadFeedStocks]);

  // Load data on component mount
  useEffect(() => {
    loadFeedStocks();
  }, [loadFeedStocks]);

  return {
    // Data state
    feedStocks,
    loading,
    error,
    
    // Statistics
    statistics,
    
    // CRUD operations
    loadFeedStocks,
    createFeedStock,
    updateFeedStock,
    deleteFeedStock,
    
    // Transaction operations
    recordUsage,
    recordCount,
    restockItem,
    
    // Utility functions
    getFeedStockById,
    getFeedStockStatus,
    isExpiringSoon,
    isExpired,
    
    // Refresh
    refreshData,
  };
}; 